import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Grid,
  Inline,
  KpiCard,
  MoneyField,
  PageHeader,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  Switch,
  Tab,
  Tabs,
  TextAreaField,
  formatMoneyMinor,
  useToast
} from "@leylion/design-system";
import {
  assignPriceList,
  getBranches,
  getCatalogOverview,
  getCategories,
  getModifierGroups,
  getKitchenRoutingRules,
  getKitchenStations,
  getPriceListAssignments,
  getPriceListEntries,
  getPriceLists,
  getProducts,
  getPromotions,
  getRuntimeSettings,
  getStoredContext,
  getTaxCodes,
  getUnits,
  saveCategory,
  saveModifierGroup,
  saveKitchenRoutingRule,
  savePriceList,
  savePriceListEntry,
  saveProduct,
  uploadProductImage,
  deleteProductImage,
  savePromotion,
  saveTaxCode,
  saveUnit
} from "../api/client";
import type {
  CatalogOverview,
  Category,
  ModifierGroup,
  PriceList,
  PriceListAssignment,
  PriceListEntry,
  Product,
  ProductStatus,
  Promotion,
  TaxCode,
  UnitOfMeasure
} from "../api/catalog-types";
import type { Branch } from "../api/settings-types";
import type { KitchenRoutingRule, KitchenStation } from "../api/restaurant-types";
import "./catalog.css";

type Language = "ar" | "en";
type ProductImageCrop = { x: number; y: number; zoom: number };

const DEFAULT_PRODUCT_IMAGE_CROP: ProductImageCrop = { x: 50, y: 50, zoom: 1 };

async function cropProductImageFile(file: File, crop: ProductImageCrop): Promise<File> {
  const bitmap = await createImageBitmap(file);
  try {
    const cropSize = Math.max(1, Math.min(bitmap.width, bitmap.height) / crop.zoom);
    const maxX = Math.max(0, bitmap.width - cropSize);
    const maxY = Math.max(0, bitmap.height - cropSize);
    const sourceX = maxX * (crop.x / 100);
    const sourceY = maxY * (crop.y / 100);
    const outputSize = Math.max(1, Math.min(1200, Math.round(cropSize)));
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare the product image crop.");
    context.drawImage(
      bitmap,
      sourceX,
      sourceY,
      cropSize,
      cropSize,
      0,
      0,
      outputSize,
      outputSize
    );
    const requestedType = ["image/jpeg", "image/png", "image/webp"].includes(file.type)
      ? file.type
      : "image/jpeg";
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (value) => value ? resolve(value) : reject(new Error("Unable to encode the cropped product image.")),
        requestedType,
        requestedType === "image/png" ? undefined : 0.92
      );
    });
    return new File([blob], file.name, { type: blob.type || requestedType, lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}

type ViewKey =
  | "overview"
  | "products"
  | "structure"
  | "modifiers"
  | "pricing"
  | "tax"
  | "promotions";

export function CatalogCenter({
  language,
  permissions
}: {
  language: Language;
  permissions: string[];
}) {
  const [view, setView] = useState<ViewKey>("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [currency, setCurrency] = useState("AED");
  const ar = language === "ar";

  useEffect(() => {
    const context = getStoredContext();
    if (!context?.branchId) return;
    void getRuntimeSettings(context.branchId)
      .then((runtime) => setCurrency(runtime.currency || "AED"))
      .catch(() => undefined);
  }, []);

  const canCatalogManage = permissions.includes("catalog.manage");
  const canPricingManage = permissions.includes("pricing.manage");
  const canPricingRead = canPricingManage || permissions.includes("pricing.read");
  const canKitchenManage = permissions.includes("kds.manage");
  const canKitchenRead = canKitchenManage || permissions.includes("kds.read");
  const canPromotionsManage = permissions.includes("promotions.manage");

  const items: Array<[ViewKey, string]> = [
    ["overview", ar ? "نظرة عامة" : "Overview"],
    ["products", ar ? "المنتجات والمتغيرات" : "Products & variants"],
    ["structure", ar ? "الفئات والوحدات" : "Categories & units"],
    ["modifiers", ar ? "الإضافات والخيارات" : "Modifiers"],
    ["pricing", ar ? "قوائم الأسعار" : "Price lists"],
    ["tax", ar ? "الضرائب" : "Tax codes"],
    ["promotions", ar ? "العروض" : "Promotions"]
  ];

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "إدارة الكتالوج التجاري" : "Commercial Catalog Management"}
        title={ar ? "المنتجات والتسعير والضرائب" : "Catalog, Pricing & Tax"}
        description={
          ar
            ? "مصدر موحد للمنتجات والمتغيرات والباركود والإضافات وقوائم الأسعار والضريبة والعروض، مرتبط مباشرة بالكاشير."
            : "A unified source for products, variants, barcodes, modifiers, price lists, tax and promotions, connected directly to POS."
        }
      />

      <div className="catalog-layout">
        <aside className="catalog-navigation">
          {items.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={view === key ? "is-active" : ""}
              onClick={() => setView(key)}
            >
              {label}
            </button>
          ))}
        </aside>

        <main className="catalog-content">
          {view === "overview" ? (
            <CatalogOverviewPanel
              language={language}
              refreshKey={refreshKey}
              onOpen={setView}
            />
          ) : view === "products" ? (
            <ProductsPanel
              language={language}
              currency={currency}
              canManage={canCatalogManage}
              canPricingRead={canPricingRead}
              canPricingManage={canPricingManage}
              canKitchenRead={canKitchenRead}
              canKitchenManage={canKitchenManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "structure" ? (
            <StructurePanel
              language={language}
              canManage={canCatalogManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "modifiers" ? (
            <ModifiersPanel
              language={language}
              currency={currency}
              canManage={canCatalogManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "pricing" ? (
            <PricingPanel
              language={language}
              currency={currency}
              canManage={canPricingManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "tax" ? (
            <TaxPanel
              language={language}
              canManage={canPricingManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : (
            <PromotionsPanel
              language={language}
              currency={currency}
              canManage={canPromotionsManage}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          )}
        </main>
      </div>
    </Stack>
  );
}

function CatalogOverviewPanel({
  language,
  refreshKey,
  onOpen
}: {
  language: Language;
  refreshKey: number;
  onOpen: (key: ViewKey) => void;
}) {
  const [overview, setOverview] = useState<CatalogOverview | null>(null);
  const [error, setError] = useState("");
  const ar = language === "ar";

  useEffect(() => {
    getCatalogOverview()
      .then(setOverview)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Unable to load catalog.")
      );
  }, [refreshKey]);

  if (!overview) {
    return error ? (
      <Alert tone="danger" title={ar ? "تعذر التحميل" : "Load failed"}>{error}</Alert>
    ) : (
      <Card><Inline><Spinner label="Loading" />{ar ? "جارٍ تحميل الكتالوج…" : "Loading catalog…"}</Inline></Card>
    );
  }

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard label={ar ? "الفئات" : "Categories"} value={overview.categories} />
        <KpiCard label={ar ? "المنتجات النشطة" : "Active products"} value={overview.activeProducts} />
        <KpiCard label={ar ? "المتغيرات" : "Variants"} value={overview.activeVariants} />
        <KpiCard label={ar ? "مجموعات الإضافات" : "Modifier groups"} value={overview.modifierGroups} />
        <KpiCard label={ar ? "قوائم الأسعار" : "Price lists"} value={overview.priceLists} />
        <KpiCard label={ar ? "الرموز الضريبية" : "Tax codes"} value={overview.taxCodes} />
        <KpiCard label={ar ? "العروض النشطة" : "Active promotions"} value={overview.activePromotions} />
        <KpiCard
          label={ar ? "منتجات دون سعر" : "Products without price"}
          value={overview.productsWithoutPrice}
          trendTone={overview.productsWithoutPrice ? "danger" : "success"}
        />
      </Grid>

      {overview.warnings.length ? (
        <Alert tone="warning" title={ar ? "ملاحظات الجاهزية" : "Readiness warnings"}>
          {overview.warnings.join(" · ")}
        </Alert>
      ) : (
        <Alert tone="success" title={ar ? "الكتالوج التجاري جاهز" : "Commercial catalog healthy"}>
          {ar
            ? "كل المنتجات النشطة لديها تسعير وضريبة فعالة."
            : "All active products have effective pricing and tax configuration."}
        </Alert>
      )}

      <Grid columns={3}>
        <Card interactive title={ar ? "إدارة المنتج" : "Product management"} description={ar ? "فئات، منتجات، متغيرات، SKU وباركود." : "Categories, products, variants, SKU and barcodes."} onClick={() => onOpen("products")} />
        <Card interactive title={ar ? "التسعير الفعّال" : "Effective pricing"} description={ar ? "قوائم أسعار وفروع وتواريخ صلاحية." : "Price lists, branches and effective dating."} onClick={() => onOpen("pricing")} />
        <Card interactive title={ar ? "محرك الضرائب والعروض" : "Tax & promotion engine"} description={ar ? "ضريبة شاملة أو غير شاملة وعروض مؤهلة حسب المنتج." : "Inclusive/exclusive tax and product-targeted promotions."} onClick={() => onOpen("tax")} />
      </Grid>
    </Stack>
  );
}

function ProductsPanel({
  language,
  currency,
  canManage,
  canPricingRead,
  canPricingManage,
  canKitchenRead,
  canKitchenManage,
  onChanged
}: {
  language: Language;
  currency: string;
  canManage: boolean;
  canPricingRead: boolean;
  canPricingManage: boolean;
  canKitchenRead: boolean;
  canKitchenManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<UnitOfMeasure[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
  const [defaultPriceList, setDefaultPriceList] = useState<PriceList | null>(null);
  const [priceEntries, setPriceEntries] = useState<PriceListEntry[]>([]);
  const [stations, setStations] = useState<KitchenStation[]>([]);
  const [routingRules, setRoutingRules] = useState<KitchenRoutingRule[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ProductStatus | "">("");
  const [editor, setEditor] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState(false);
  const [productImageFile, setProductImageFile] = useState<File | null>(null);
  const [productImagePreview, setProductImagePreview] = useState("");
  const [productImageCrop, setProductImageCrop] = useState<ProductImageCrop>(DEFAULT_PRODUCT_IMAGE_CROP);
  const productImageDrag = useRef<{ pointerId: number; x: number; y: number; cropX: number; cropY: number } | null>(null);
  const [removeProductImage, setRemoveProductImage] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [productItems, categoryItems, unitItems, taxItems, modifierItems, priceLists, stationItems, routeItems] = await Promise.all([
        getProducts(search || undefined, undefined, status || undefined),
        getCategories(),
        getUnits(),
        getTaxCodes(),
        getModifierGroups(),
        canPricingRead ? getPriceLists() : Promise.resolve([] as PriceList[]),
        canKitchenRead ? getKitchenStations() : Promise.resolve([] as KitchenStation[]),
        canKitchenRead ? getKitchenRoutingRules() : Promise.resolve([] as KitchenRoutingRule[])
      ]);

      const directPriceList = priceLists.find((item) => item.isDefault && item.isActive && item.currency === currency)
        ?? priceLists.find((item) => item.isDefault && item.isActive)
        ?? null;
      const entryItems = directPriceList ? await getPriceListEntries(directPriceList.id) : [];
      const now = Date.now();
      const effectivePrice = (variantId: string) => entryItems
        .filter((entry) => entry.productVariantId === variantId
          && Date.parse(entry.effectiveFrom) <= now
          && (!entry.effectiveUntil || Date.parse(entry.effectiveUntil) > now))
        .sort((left, right) => Date.parse(right.effectiveFrom) - Date.parse(left.effectiveFrom))[0];
      const productRoute = (productId: string) => routeItems.find((rule) =>
        rule.productId === productId && !rule.productVariantId && !rule.categoryId && rule.isActive);

      setProducts(productItems.map((product) => ({
        ...product,
        preparationStationId: productRoute(product.id)?.stationId ?? null,
        variants: product.variants.map((variant) => ({
          ...variant,
          salePriceMinor: effectivePrice(variant.id ?? "")?.unitPriceMinor ?? null
        }))
      })));
      setCategories(categoryItems);
      setUnits(unitItems);
      setTaxCodes(taxItems);
      setModifierGroups(modifierItems);
      setDefaultPriceList(directPriceList);
      setPriceEntries(entryItems);
      setStations(stationItems.filter((station) => station.isActive));
      setRoutingRules(routeItems);
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load products.");
    }
  };

  useEffect(() => { void load(); }, [status, currency]);
  useEffect(() => {
    if (!productImageFile) {
      setProductImagePreview("");
      return;
    }
    const previewUrl = URL.createObjectURL(productImageFile);
    setProductImagePreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [productImageFile]);

  const resetProductImageDraft = () => {
    setProductImageFile(null);
    setProductImagePreview("");
    setProductImageCrop(DEFAULT_PRODUCT_IMAGE_CROP);
    productImageDrag.current = null;
    setRemoveProductImage(false);
  };

  const closeProductEditor = () => {
    setEditor(null);
    setNewProduct(false);
    resetProductImageDraft();
  };

  const openNew = () => {
    resetProductImageDraft();
    const defaultTax = taxCodes.find((item) => item.isDefault) ?? taxCodes[0];
    setNewProduct(true);
    setEditor({
      id: "",
      categoryId: categories[0]?.id ?? "",
      categoryNameAr: "",
      categoryNameEn: "",
      unitOfMeasureId: units[0]?.id ?? "",
      unitCode: units[0]?.code ?? "EACH",
      taxCodeId: defaultTax?.id ?? "",
      taxCode: defaultTax?.code ?? "",
      code: "NEW-PRODUCT",
      nameAr: "منتج جديد",
      nameEn: "New Product",
      descriptionAr: null,
      descriptionEn: null,
      productType: "PreparedItem",
      status: "Draft",
      imageUrl: null,
      trackInventory: false,
      allowOpenPrice: false,
      sellable: true,
      purchasable: false,
      isFavorite: false,
      sortOrder: products.length + 1,
      preparationStationId: null,
      modifierGroupIds: [],
      variants: [{
        sku: "NEW-SKU",
        nameAr: "عادي",
        nameEn: "Regular",
        attributesJson: "{}",
        costMinor: null,
        salePriceMinor: null,
        isDefault: true,
        isActive: true,
        sortOrder: 10,
        barcodes: []
      }],
      version: 0,
      updatedAt: new Date().toISOString()
    });
  };

  const save = async () => {
    if (!editor) return;

    if (canPricingManage && editor.variants.some((variant) => variant.salePriceMinor != null) && !defaultPriceList) {
      setError(ar
        ? "لا توجد قائمة أسعار افتراضية فعالة لحفظ سعر البيع المباشر. أنشئ أو فعّل قائمة أسعار افتراضية أولاً."
        : "No active default Price List is available for direct sale prices. Configure a default Price List first.");
      return;
    }
    if (canKitchenManage && editor.preparationStationId
        && !stations.some((station) => station.id === editor.preparationStationId && station.isActive)) {
      setError(ar ? "منطقة التحضير المختارة غير فعالة." : "The selected preparation area is not active.");
      return;
    }

    let coreSaved = false;
    let uploadedImageUrl: string | null = null;
    const previousImageUrl = editor.imageUrl;
    try {
      if (productImageFile) {
        const croppedProductImage = await cropProductImageFile(productImageFile, productImageCrop);
        const uploaded = await uploadProductImage(croppedProductImage);
        uploadedImageUrl = uploaded.imageUrl;
      }
      const nextImageUrl = removeProductImage ? null : (uploadedImageUrl ?? editor.imageUrl);
      const requestedPriceBySku = new Map(editor.variants.map((variant) => [
        variant.sku.trim().toUpperCase(), variant.salePriceMinor ?? null
      ]));
      const saved = await saveProduct({
        ...editor,
        imageUrl: nextImageUrl,
        id: newProduct ? undefined : editor.id
      });
      coreSaved = true;
      if (previousImageUrl
          && previousImageUrl !== nextImageUrl
          && previousImageUrl.startsWith("/api/v1/catalog/product-images/")) {
        void deleteProductImage(previousImageUrl).catch(() => undefined);
      }

      if (canPricingManage && defaultPriceList) {
        const now = Date.now();
        for (const variant of saved.variants) {
          const requestedPrice = requestedPriceBySku.get(variant.sku.trim().toUpperCase()) ?? null;
          const existing = priceEntries
            .filter((entry) => entry.productVariantId === variant.id
              && Date.parse(entry.effectiveFrom) <= now
              && (!entry.effectiveUntil || Date.parse(entry.effectiveUntil) > now))
            .sort((left, right) => Date.parse(right.effectiveFrom) - Date.parse(left.effectiveFrom))[0];

          if (requestedPrice == null) {
            if (existing) {
              const endedAt = new Date(Math.max(Date.now(), Date.parse(existing.effectiveFrom) + 1)).toISOString();
              await savePriceListEntry(defaultPriceList.id, {
                ...existing,
                productVariantId: variant.id!,
                unitPriceMinor: existing.unitPriceMinor,
                effectiveUntil: endedAt
              });
            }
            continue;
          }
          if (existing?.unitPriceMinor === requestedPrice) continue;
          await savePriceListEntry(defaultPriceList.id, {
            ...(existing ?? {}),
            productVariantId: variant.id!,
            unitPriceMinor: requestedPrice,
            compareAtPriceMinor: existing?.compareAtPriceMinor ?? null,
            effectiveFrom: existing?.effectiveFrom ?? new Date().toISOString(),
            effectiveUntil: existing?.effectiveUntil ?? null
          });
        }
      }

      if (canKitchenManage) {
        const existingRule = routingRules.find((rule) =>
          rule.productId === saved.id && !rule.productVariantId && !rule.categoryId);
        const stationId = editor.preparationStationId ?? "";
        if (stationId) {
          if (!existingRule || !existingRule.isActive || existingRule.stationId !== stationId) {
            await saveKitchenRoutingRule({
              ...(existingRule ?? {}),
              stationId,
              productId: saved.id,
              productVariantId: null,
              categoryId: null,
              priority: existingRule?.priority ?? 100,
              isActive: true
            });
          }
        } else if (existingRule?.isActive) {
          await saveKitchenRoutingRule({
            ...existingRule,
            stationId: existingRule.stationId,
            productId: saved.id,
            productVariantId: null,
            categoryId: null,
            priority: existingRule.priority,
            isActive: false
          });
        }
      }

      setNewProduct(false);
      toast.notify({ tone: "success", title: ar ? "تم حفظ المنتج والسعر ومنطقة التحضير" : "Product, pricing and preparation saved" });
      await load();
      closeProductEditor();
      onChanged();
    } catch (caught) {
      if (!coreSaved && uploadedImageUrl) {
        await deleteProductImage(uploadedImageUrl).catch(() => undefined);
      }
      const detail = caught instanceof Error ? caught.message : "Unable to save product.";
      if (coreSaved) {
        await load().catch(() => undefined);
        closeProductEditor();
      }
      setError(coreSaved
        ? (ar
          ? `تم حفظ بيانات المنتج الأساسية، لكن تعذر إكمال السعر أو منطقة التحضير: ${detail}`
          : `The core product was saved, but pricing or preparation setup could not be completed: ${detail}`)
        : detail);
    }
  };

  const updateVariant = (index: number, patch: Partial<Product["variants"][number]>) => {
    if (!editor) return;
    setEditor({
      ...editor,
      variants: editor.variants.map((variant, itemIndex) =>
        itemIndex === index ? { ...variant, ...patch } : variant
      )
    });
  };

  return (
    <Stack>
      <Panel
        title={ar ? "المنتجات والمتغيرات" : "Products & Variants"}
        description={ar ? "منتج واحد يمكن أن يحتوي متغيرات متعددة وSKU وباركود وإضافات." : "One product can own multiple variants, SKU, barcodes and modifier groups."}
        actions={canManage ? <Button onClick={openNew}>{ar ? "منتج جديد" : "New product"}</Button> : null}
      >
        <Stack>
          {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
          <div className="catalog-filters">
            <Field label={ar ? "بحث" : "Search"} value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && void load()} />
            <SelectField label={ar ? "الحالة" : "Status"} value={status} onChange={(event) => setStatus(event.target.value as ProductStatus | "")}>
              <option value="">{ar ? "جميع الحالات" : "All statuses"}</option>
              {["Draft", "Active", "Inactive", "Archived"].map((value) => <option key={value}>{value}</option>)}
            </SelectField>
            <Button variant="secondary" onClick={() => void load()}>{ar ? "تطبيق" : "Apply"}</Button>
          </div>

          <DataTable
            rows={products}
            rowKey={(row) => row.id}
            columns={[
              { key: "product", header: ar ? "المنتج" : "Product", cell: (row) => <button className="catalog-link" onClick={() => { resetProductImageDraft(); setNewProduct(false); setEditor(structuredClone(row)); }}><strong>{ar ? row.nameAr : row.nameEn}</strong><small>{row.code} · {row.categoryNameEn}</small></button> },
              { key: "variants", header: ar ? "المتغيرات" : "Variants", cell: (row) => row.variants.length },
              { key: "tax", header: ar ? "الضريبة" : "Tax", cell: (row) => row.taxCode },
              { key: "stock", header: ar ? "المخزون" : "Inventory", cell: (row) => <StatusBadge tone={row.trackInventory ? "info" : "neutral"}>{row.trackInventory ? (ar ? "متتبع" : "Tracked") : (ar ? "غير متتبع" : "Not tracked")}</StatusBadge> },
              { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : row.status === "Draft" ? "warning" : "neutral"}>{row.status}</StatusBadge> }
            ]}
          />
        </Stack>
      </Panel>

      <Dialog
        open={Boolean(editor)}
        onClose={closeProductEditor}
        title={newProduct ? (ar ? "منتج جديد" : "New product") : (ar ? "تعديل المنتج" : "Edit product")}
        footer={<Inline><Button variant="secondary" onClick={closeProductEditor}>{ar ? "إغلاق" : "Close"}</Button><Button disabled={!canManage} onClick={() => void save()}>{ar ? "حفظ المنتج" : "Save product"}</Button></Inline>}
      >
        {editor ? (
          <Stack>
            <section className="catalog-product-image-editor" aria-label={ar ? "صورة المنتج" : "Product image"}>
              <div
                className={`catalog-product-image-editor__preview${productImageFile ? " is-cropping" : ""}`}
                data-testid="product-image-crop-preview"
                onPointerDown={(event) => {
                  if (!productImageFile) return;
                  productImageDrag.current = {
                    pointerId: event.pointerId,
                    x: event.clientX,
                    y: event.clientY,
                    cropX: productImageCrop.x,
                    cropY: productImageCrop.y
                  };
                  event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerMove={(event) => {
                  const drag = productImageDrag.current;
                  if (!productImageFile || !drag || drag.pointerId !== event.pointerId) return;
                  const bounds = event.currentTarget.getBoundingClientRect();
                  const deltaX = ((event.clientX - drag.x) / Math.max(bounds.width, 1)) * 100 / productImageCrop.zoom;
                  const deltaY = ((event.clientY - drag.y) / Math.max(bounds.height, 1)) * 100 / productImageCrop.zoom;
                  setProductImageCrop((current) => ({
                    ...current,
                    x: Math.max(0, Math.min(100, drag.cropX - deltaX)),
                    y: Math.max(0, Math.min(100, drag.cropY - deltaY))
                  }));
                }}
                onPointerUp={(event) => {
                  if (productImageDrag.current?.pointerId === event.pointerId) productImageDrag.current = null;
                }}
                onPointerCancel={() => { productImageDrag.current = null; }}
              >
                {(productImagePreview || (!removeProductImage && editor.imageUrl)) ? (
                  <img
                    src={productImagePreview || editor.imageUrl || ""}
                    alt={ar ? `صورة ${editor.nameAr}` : `${editor.nameEn} image`}
                    style={productImageFile ? {
                      objectPosition: `${productImageCrop.x}% ${productImageCrop.y}%`,
                      transform: `scale(${productImageCrop.zoom})`,
                      transformOrigin: `${productImageCrop.x}% ${productImageCrop.y}%`
                    } : undefined}
                  />
                ) : (
                  <span aria-hidden="true">▧</span>
                )}
                {productImageFile ? <span className="catalog-product-image-editor__crop-grid" aria-hidden="true" /> : null}
              </div>
              <div className="catalog-product-image-editor__copy">
                <strong>{ar ? "صورة المنتج — اختيارية" : "Product image — optional"}</strong>
                <small>{ar ? "JPG أو PNG أو WebP، بحد أقصى 5 MB. اسحب الصورة داخل المربع لاختيار موضعها ثم استخدم التكبير عند الحاجة." : "JPG, PNG or WebP, up to 5 MB. Drag inside the square to frame the product, then zoom when needed."}</small>
                {productImageFile ? (
                  <div className="catalog-product-image-editor__crop-controls">
                    <label>
                      <span>{ar ? "التكبير" : "Zoom"}</span>
                      <input
                        data-testid="product-image-crop-zoom"
                        type="range"
                        min="1"
                        max="2.5"
                        step="0.05"
                        value={productImageCrop.zoom}
                        onChange={(event) => setProductImageCrop((current) => ({ ...current, zoom: Number(event.target.value) }))}
                      />
                      <strong>{Math.round(productImageCrop.zoom * 100)}%</strong>
                    </label>
                    <button
                      type="button"
                      className="catalog-product-image-editor__reset"
                      onClick={() => setProductImageCrop(DEFAULT_PRODUCT_IMAGE_CROP)}
                    >
                      {ar ? "إعادة التمركز" : "Reset position"}
                    </button>
                  </div>
                ) : null}
                <Inline>
                  <label className="catalog-product-image-editor__upload">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        setProductImageFile(file);
                        if (file) {
                          setProductImageCrop(DEFAULT_PRODUCT_IMAGE_CROP);
                          setRemoveProductImage(false);
                        }
                        event.currentTarget.value = "";
                      }}
                    />
                    <span>{productImageFile || editor.imageUrl ? (ar ? "تغيير الصورة" : "Change image") : (ar ? "رفع صورة" : "Upload image")}</span>
                  </label>
                  {(productImageFile || (!removeProductImage && editor.imageUrl)) ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setProductImageFile(null);
                        setProductImageCrop(DEFAULT_PRODUCT_IMAGE_CROP);
                        setRemoveProductImage(Boolean(editor.imageUrl));
                      }}
                    >
                      {ar ? "حذف الصورة" : "Remove image"}
                    </Button>
                  ) : null}
                </Inline>
              </div>
            </section>
            <Grid columns={2}>
              <Field label={ar ? "رمز المنتج" : "Product code"} value={editor.code} onChange={(e) => setEditor({ ...editor, code: e.target.value.toUpperCase() })} />
              <SelectField label={ar ? "الحالة" : "Status"} value={editor.status} onChange={(e) => setEditor({ ...editor, status: e.target.value as ProductStatus })}>
                {["Draft", "Active", "Inactive", "Archived"].map((value) => <option key={value}>{value}</option>)}
              </SelectField>
              <Field label={ar ? "الاسم العربي" : "Arabic name"} value={editor.nameAr} onChange={(e) => setEditor({ ...editor, nameAr: e.target.value })} />
              <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={editor.nameEn} onChange={(e) => setEditor({ ...editor, nameEn: e.target.value })} />
              <SelectField label={ar ? "الفئة" : "Category"} value={editor.categoryId} onChange={(e) => setEditor({ ...editor, categoryId: e.target.value })}>
                {categories.map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}
              </SelectField>
              <SelectField label={ar ? "الوحدة" : "Unit"} value={editor.unitOfMeasureId} onChange={(e) => setEditor({ ...editor, unitOfMeasureId: e.target.value })}>
                {units.map((item) => <option key={item.id} value={item.id}>{item.code} · {ar ? item.nameAr : item.nameEn}</option>)}
              </SelectField>
              <SelectField label={ar ? "رمز الضريبة" : "Tax code"} value={editor.taxCodeId} onChange={(e) => setEditor({ ...editor, taxCodeId: e.target.value })}>
                {taxCodes.map((item) => <option key={item.id} value={item.id}>{item.code} · {ar ? item.nameAr : item.nameEn}</option>)}
              </SelectField>
              <SelectField label={ar ? "نوع المنتج" : "Product type"} value={editor.productType} onChange={(e) => setEditor({ ...editor, productType: e.target.value as Product["productType"] })}>
                {["StockItem", "PreparedItem", "Service", "Bundle"].map((value) => <option key={value}>{value}</option>)}
              </SelectField>
              {canKitchenRead ? (
                <SelectField
                  label={ar ? "منطقة التحضير" : "Preparation area"}
                  value={editor.preparationStationId ?? ""}
                  disabled={!canKitchenManage}
                  onChange={(e) => setEditor({ ...editor, preparationStationId: e.target.value || null })}
                >
                  <option value="">{ar ? "تلقائي — المحطة الافتراضية" : "Automatic — default station"}</option>
                  {stations.map((station) => <option key={station.id} value={station.id}>{ar ? station.nameAr : station.nameEn} · {station.code}</option>)}
                </SelectField>
              ) : null}
              <Switch label={ar ? "تتبع المخزون" : "Track inventory"} checked={editor.trackInventory} onChange={(e) => setEditor({ ...editor, trackInventory: e.target.checked })} />
              <Switch label={ar ? "سعر مفتوح" : "Open price"} checked={editor.allowOpenPrice} onChange={(e) => setEditor({ ...editor, allowOpenPrice: e.target.checked })} />
              <Switch label={ar ? "قابل للبيع" : "Sellable"} checked={editor.sellable} onChange={(e) => setEditor({ ...editor, sellable: e.target.checked })} />
              <Switch label={ar ? "مفضّل في الكاشير" : "POS favorite"} checked={editor.isFavorite} onChange={(e) => setEditor({ ...editor, isFavorite: e.target.checked })} />
            </Grid>

            <Card title={ar ? "مجموعات الإضافات" : "Modifier groups"}>
              <div className="catalog-check-grid">
                {modifierGroups.map((group) => (
                  <Checkbox
                    key={group.id}
                    label={ar ? group.nameAr : group.nameEn}
                    checked={editor.modifierGroupIds.includes(group.id)}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        modifierGroupIds: event.target.checked
                          ? [...editor.modifierGroupIds, group.id]
                          : editor.modifierGroupIds.filter((id) => id !== group.id)
                      })
                    }
                  />
                ))}
              </div>
            </Card>

            <Card
              title={ar ? "المتغيرات وSKU" : "Variants & SKU"}
              actions={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setEditor({
                      ...editor,
                      variants: [
                        ...editor.variants,
                        {
                          sku: `${editor.code}-${editor.variants.length + 1}`,
                          nameAr: "متغير جديد",
                          nameEn: "New Variant",
                          attributesJson: "{}",
                          costMinor: null,
                          salePriceMinor: null,
                          isDefault: false,
                          isActive: true,
                          sortOrder: (editor.variants.length + 1) * 10,
                          barcodes: []
                        }
                      ]
                    })
                  }
                >
                  {ar ? "إضافة متغير" : "Add variant"}
                </Button>
              }
            >
              <Stack>
                {editor.variants.map((variant, index) => (
                  <div className="variant-editor" key={variant.id ?? index}>
                    <Grid columns={2}>
                      <Field label="SKU" value={variant.sku} onChange={(e) => updateVariant(index, { sku: e.target.value.toUpperCase() })} />
                      <Switch
                        label={ar ? "المتغير الافتراضي" : "Default variant"}
                        checked={variant.isDefault}
                        onChange={(e) =>
                          setEditor({
                            ...editor,
                            variants: editor.variants.map((item, itemIndex) => ({
                              ...item,
                              isDefault: itemIndex === index ? e.target.checked : false
                            }))
                          })
                        }
                      />
                      <Field label={ar ? "الاسم العربي" : "Arabic name"} value={variant.nameAr} onChange={(e) => updateVariant(index, { nameAr: e.target.value })} />
                      <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={variant.nameEn} onChange={(e) => updateVariant(index, { nameEn: e.target.value })} />
                      {canPricingRead ? (
                        <MoneyField
                          label={ar ? "سعر البيع" : "Sale price"}
                          hint={defaultPriceList
                            ? (ar ? `القائمة الافتراضية: ${defaultPriceList.nameAr}` : `Default list: ${defaultPriceList.nameEn}`)
                            : (ar ? "يتطلب قائمة أسعار افتراضية فعالة" : "Requires an active default Price List")}
                          valueMinor={variant.salePriceMinor}
                          currency={defaultPriceList?.currency ?? currency}
                          nullable
                          disabled={!canPricingManage || !defaultPriceList}
                          onMinorChange={(value) => updateVariant(index, { salePriceMinor: value })}
                        />
                      ) : null}
                      <MoneyField label={ar ? "التكلفة" : "Cost"} valueMinor={variant.costMinor} currency={currency} nullable onMinorChange={(value) => updateVariant(index, { costMinor: value })} />
                      <Field label={ar ? "الباركود الأساسي" : "Primary barcode"} value={variant.barcodes[0]?.barcode ?? ""} onChange={(e) => updateVariant(index, { barcodes: e.target.value ? [{ id: variant.barcodes[0]?.id, barcode: e.target.value, barcodeType: "EAN13", isPrimary: true }] : [] })} />
                    </Grid>
                  </div>
                ))}
              </Stack>
            </Card>
          </Stack>
        ) : null}
      </Dialog>
    </Stack>
  );
}

function StructurePanel({
  language,
  canManage,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<UnitOfMeasure[]>([]);
  const [categoryEditor, setCategoryEditor] = useState<Partial<Category> | null>(null);
  const [unitEditor, setUnitEditor] = useState<Partial<UnitOfMeasure> | null>(null);

  const load = async () => {
    const [categoryItems, unitItems] = await Promise.all([getCategories(), getUnits()]);
    setCategories(categoryItems);
    setUnits(unitItems);
  };
  useEffect(() => { void load(); }, []);

  const saveCat = async () => {
    if (!categoryEditor) return;
    await saveCategory({
      ...categoryEditor,
      code: categoryEditor.code ?? "",
      nameAr: categoryEditor.nameAr ?? "",
      nameEn: categoryEditor.nameEn ?? ""
    });
    setCategoryEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ الفئة" : "Category saved" });
    await load();
    onChanged();
  };

  const saveUom = async () => {
    if (!unitEditor) return;
    await saveUnit({
      ...unitEditor,
      code: unitEditor.code ?? "",
      nameAr: unitEditor.nameAr ?? "",
      nameEn: unitEditor.nameEn ?? ""
    });
    setUnitEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ الوحدة" : "Unit saved" });
    await load();
    onChanged();
  };

  return (
    <Grid columns={2}>
      <Panel title={ar ? "الفئات" : "Categories"} actions={canManage ? <Button size="sm" onClick={() => setCategoryEditor({ code: "NEW", nameAr: "فئة جديدة", nameEn: "New Category", isActive: true, sortOrder: categories.length + 1 })}>{ar ? "إضافة" : "Add"}</Button> : null}>
        <DataTable
          rows={categories}
          rowKey={(row) => row.id}
          columns={[
            { key: "name", header: ar ? "الفئة" : "Category", cell: (row) => <button className="catalog-link" onClick={() => setCategoryEditor(row)}><strong>{ar ? row.nameAr : row.nameEn}</strong><small>{row.code}</small></button> },
            { key: "count", header: ar ? "المنتجات" : "Products", cell: (row) => row.productCount },
            { key: "active", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? "Active" : "Inactive"}</StatusBadge> }
          ]}
        />
      </Panel>
      <Panel title={ar ? "وحدات القياس" : "Units of Measure"} actions={canManage ? <Button size="sm" onClick={() => setUnitEditor({ code: "NEW", nameAr: "وحدة جديدة", nameEn: "New Unit", precision: 0, isFractional: false, isActive: true })}>{ar ? "إضافة" : "Add"}</Button> : null}>
        <DataTable
          rows={units}
          rowKey={(row) => row.id}
          columns={[
            { key: "code", header: ar ? "الرمز" : "Code", cell: (row) => <button className="catalog-link" onClick={() => setUnitEditor(row)}><strong>{row.code}</strong><small>{ar ? row.nameAr : row.nameEn}</small></button> },
            { key: "precision", header: ar ? "الدقة" : "Precision", cell: (row) => row.precision },
            { key: "fractional", header: ar ? "كسري" : "Fractional", cell: (row) => row.isFractional ? "Yes" : "No" }
          ]}
        />
      </Panel>

      <Dialog open={Boolean(categoryEditor)} onClose={() => setCategoryEditor(null)} title={ar ? "الفئة" : "Category"} footer={<Inline><Button variant="secondary" onClick={() => setCategoryEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={() => void saveCat()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {categoryEditor ? <Grid columns={2}><Field label={ar ? "الرمز" : "Code"} value={categoryEditor.code ?? ""} onChange={(e) => setCategoryEditor({ ...categoryEditor, code: e.target.value.toUpperCase() })} /><Switch label={ar ? "نشطة" : "Active"} checked={categoryEditor.isActive ?? true} onChange={(e) => setCategoryEditor({ ...categoryEditor, isActive: e.target.checked })} /><Field label={ar ? "الاسم العربي" : "Arabic name"} value={categoryEditor.nameAr ?? ""} onChange={(e) => setCategoryEditor({ ...categoryEditor, nameAr: e.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={categoryEditor.nameEn ?? ""} onChange={(e) => setCategoryEditor({ ...categoryEditor, nameEn: e.target.value })} /></Grid> : null}
      </Dialog>

      <Dialog open={Boolean(unitEditor)} onClose={() => setUnitEditor(null)} title={ar ? "وحدة القياس" : "Unit of measure"} footer={<Inline><Button variant="secondary" onClick={() => setUnitEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={() => void saveUom()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {unitEditor ? <Grid columns={2}><Field label={ar ? "الرمز" : "Code"} value={unitEditor.code ?? ""} onChange={(e) => setUnitEditor({ ...unitEditor, code: e.target.value.toUpperCase() })} /><Field label={ar ? "الدقة" : "Precision"} type="number" min={0} max={6} value={unitEditor.precision ?? 0} onChange={(e) => setUnitEditor({ ...unitEditor, precision: Number(e.target.value) })} /><Field label={ar ? "الاسم العربي" : "Arabic name"} value={unitEditor.nameAr ?? ""} onChange={(e) => setUnitEditor({ ...unitEditor, nameAr: e.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={unitEditor.nameEn ?? ""} onChange={(e) => setUnitEditor({ ...unitEditor, nameEn: e.target.value })} /><Switch label={ar ? "تقبل الكسور" : "Fractional"} checked={unitEditor.isFractional ?? false} onChange={(e) => setUnitEditor({ ...unitEditor, isFractional: e.target.checked })} /></Grid> : null}
      </Dialog>
    </Grid>
  );
}

function ModifiersPanel({
  language,
  currency,
  canManage,
  onChanged
}: {
  language: Language;
  currency: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [groups, setGroups] = useState<ModifierGroup[]>([]);
  const [editor, setEditor] = useState<Partial<ModifierGroup> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const savingRef = useRef(false);
  const defaultCount = editor?.options?.filter(option => option.isActive && option.isDefault).length ?? 0;
  const minimum = editor?.minimumSelections ?? 0;
  const maximum = editor?.maximumSelections ?? 1;
  const limitsInvalid = !Number.isInteger(minimum) || !Number.isInteger(maximum)
    || minimum < 0 || maximum < minimum || maximum > 50 || (editor?.isRequired && minimum === 0);
  const defaultsError = limitsInvalid
    ? (ar ? "راجع الحد الأدنى والأعلى للاختيارات." : "Review the minimum and maximum selections.")
    : defaultCount < minimum
      ? (ar ? `اختر ${minimum} خيارات افتراضية نشطة على الأقل.` : `Select at least ${minimum} active default options.`)
      : defaultCount > maximum
        ? (ar ? `لا يمكن تجاوز ${maximum} خيارات افتراضية نشطة.` : `Active default options cannot exceed ${maximum}.`)
        : "";
  const openEditor = (value: Partial<ModifierGroup>) => { setSaveError(false); setEditor(value); };
  const closeEditor = () => { if (!savingRef.current) { setEditor(null); setSaveError(false); } };

  const load = async () => setGroups(await getModifierGroups());
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!editor || !canManage || defaultsError || savingRef.current) return;
    savingRef.current = true;
    setSaving(true); setSaveError(false);
    try {
      await saveModifierGroup({
        ...editor,
        code: editor.code ?? "",
        nameAr: editor.nameAr ?? "",
        nameEn: editor.nameEn ?? "",
        options: editor.options ?? []
      });
    } catch {
      // Never render an exception, response body or identifiers from the server.
      // A network failure does not prove that the write failed on the server.
      setSaveError(true);
      return;
    } finally { savingRef.current = false; setSaving(false); }
    setEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ مجموعة الإضافات" : "Modifier group saved" });
    await load().catch(() => toast.notify({ tone: "warning", title: ar ? "تم الحفظ؛ تعذر تحديث القائمة. أعد فتحها للتحقق." : "Saved; the list could not refresh. Reopen it to verify." }));
    onChanged();
  };

  return (
    <Panel
      title={ar ? "مجموعات الإضافات والخيارات" : "Modifier Groups & Options"}
      actions={canManage ? <Button onClick={() => openEditor({ code: "NEW-GROUP", nameAr: "مجموعة جديدة", nameEn: "New Group", minimumSelections: 0, maximumSelections: 1, isRequired: false, isActive: true, sortOrder: groups.length + 1, options: [] })}>{ar ? "مجموعة جديدة" : "New group"}</Button> : null}
    >
      <div className="modifier-grid">
        {groups.map((group) => (
          <Card key={group.id} title={ar ? group.nameAr : group.nameEn} actions={<Button size="sm" variant="secondary" onClick={() => openEditor(structuredClone(group))}>{ar ? "تعديل" : "Edit"}</Button>}>
            <Stack>
              <Inline><StatusBadge tone={group.isRequired ? "warning" : "neutral"}>{group.isRequired ? (ar ? "إلزامية" : "Required") : (ar ? "اختيارية" : "Optional")}</StatusBadge><StatusBadge tone="neutral">{group.minimumSelections}-{group.maximumSelections}</StatusBadge></Inline>
              {group.options.map((option) => <div className="modifier-option-row" key={option.id}><span>{ar ? option.nameAr : option.nameEn}</span><strong>{formatMoneyMinor(option.priceDeltaMinor, currency, ar ? "ar-AE" : "en-AE")}</strong></div>)}
            </Stack>
          </Card>
        ))}
      </div>

      <Dialog open={Boolean(editor)} onClose={closeEditor} title={ar ? "مجموعة الإضافات" : "Modifier group"} footer={<Inline><Button variant="secondary" disabled={saving} onClick={closeEditor}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage || saving || Boolean(defaultsError)} onClick={() => void save()}>{saving ? (ar ? "جارٍ الحفظ…" : "Saving…") : (ar ? "حفظ" : "Save")}</Button></Inline>}>
        {editor ? (
          <fieldset disabled={saving || !canManage} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <Stack>
            {defaultsError ? <p role="alert">{defaultsError}</p> : null}
            {saveError ? <p role="alert">{ar ? "تعذر تأكيد حفظ المجموعة. مدخلاتك محفوظة هنا؛ تحقق من الاتصال وراجع القائمة قبل إعادة المحاولة." : "Could not confirm the group was saved. Your entries remain here; check your connection and review the list before trying again."}</p> : null}
            <Grid columns={2}>
              <Field label={ar ? "الرمز" : "Code"} value={editor.code ?? ""} onChange={(e) => setEditor({ ...editor, code: e.target.value.toUpperCase() })} />
              <Switch label={ar ? "إلزامية" : "Required"} checked={editor.isRequired ?? false} onChange={(e) => setEditor({ ...editor, isRequired: e.target.checked, minimumSelections: e.target.checked ? Math.max(editor.minimumSelections ?? 0, 1) : editor.minimumSelections ?? 0 })} />
              <Field label={ar ? "الاسم العربي" : "Arabic name"} value={editor.nameAr ?? ""} onChange={(e) => setEditor({ ...editor, nameAr: e.target.value })} />
              <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={editor.nameEn ?? ""} onChange={(e) => setEditor({ ...editor, nameEn: e.target.value })} />
              <Field label={ar ? "الحد الأدنى" : "Minimum"} type="number" min={0} value={editor.minimumSelections ?? 0} onChange={(e) => setEditor({ ...editor, minimumSelections: Number(e.target.value) })} />
              <Field label={ar ? "الحد الأعلى" : "Maximum"} type="number" min={1} value={editor.maximumSelections ?? 1} onChange={(e) => setEditor({ ...editor, maximumSelections: Number(e.target.value) })} />
            </Grid>
            <Card title={ar ? "الخيارات" : "Options"} actions={<Button size="sm" variant="secondary" onClick={() => setEditor({ ...editor, options: [...(editor.options ?? []), { code: `OPTION-${(editor.options?.length ?? 0) + 1}`, nameAr: "خيار جديد", nameEn: "New Option", priceDeltaMinor: 0, taxCodeId: null, isDefault: false, isActive: true, sortOrder: (editor.options?.length ?? 0) + 1 }] })}>{ar ? "إضافة خيار" : "Add option"}</Button>}>
              <Stack>
                {(editor.options ?? []).map((option, index) => (
                  <Grid columns={3} key={option.id ?? index}>
                    <Field label={ar ? "الاسم العربي" : "Arabic name"} value={option.nameAr} onChange={(e) => setEditor({ ...editor, options: editor.options!.map((item, itemIndex) => itemIndex === index ? { ...item, nameAr: e.target.value } : item) })} />
                    <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={option.nameEn} onChange={(e) => setEditor({ ...editor, options: editor.options!.map((item, itemIndex) => itemIndex === index ? { ...item, nameEn: e.target.value } : item) })} />
                    <MoneyField label={ar ? "فرق السعر" : "Price delta"} valueMinor={option.priceDeltaMinor} currency={currency} onMinorChange={(value) => setEditor({ ...editor, options: editor.options!.map((item, itemIndex) => itemIndex === index ? { ...item, priceDeltaMinor: value ?? 0 } : item) })} />
                    <Checkbox label={ar ? `خيار افتراضي: ${option.nameAr}` : `Default option: ${option.nameEn}`} checked={option.isDefault}
                      disabled={!option.isActive || (!option.isDefault && defaultCount >= maximum)}
                      onChange={(e) => setEditor({ ...editor, options: editor.options!.map((item, itemIndex) => itemIndex === index ? { ...item, isDefault: e.target.checked } : item) })} />
                  </Grid>
                ))}
              </Stack>
            </Card>
          </Stack>
          </fieldset>
        ) : null}
      </Dialog>
    </Panel>
  );
}

function PricingPanel({
  language,
  currency,
  canManage,
  onChanged
}: {
  language: Language;
  currency: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [lists, setLists] = useState<PriceList[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [assignments, setAssignments] = useState<PriceListAssignment[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [entries, setEntries] = useState<PriceListEntry[]>([]);
  const [listEditor, setListEditor] = useState<Partial<PriceList> | null>(null);
  const [entryEditor, setEntryEditor] = useState<Partial<PriceListEntry> | null>(null);
  const [assignmentEditor, setAssignmentEditor] = useState<Partial<PriceListAssignment> | null>(null);

  const load = async () => {
    const [listItems, productItems, branchItems, assignmentItems] = await Promise.all([
      getPriceLists(),
      getProducts(),
      getBranches().catch(() => []),
      getPriceListAssignments()
    ]);
    setLists(listItems);
    setProducts(productItems);
    setBranches(branchItems);
    setAssignments(assignmentItems);
    setSelectedId((value) => value || listItems[0]?.id || "");
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => { if (selectedId) void getPriceListEntries(selectedId).then(setEntries); }, [selectedId]);

  const saveList = async () => {
    if (!listEditor) return;
    await savePriceList({
      ...listEditor,
      code: listEditor.code ?? "",
      nameAr: listEditor.nameAr ?? "",
      nameEn: listEditor.nameEn ?? "",
      scope: listEditor.scope ?? "Organization",
      currency: listEditor.currency ?? "AED"
    });
    setListEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ قائمة الأسعار" : "Price list saved" });
    await load();
    onChanged();
  };

  const saveEntry = async () => {
    if (!entryEditor || !selectedId) return;
    await savePriceListEntry(selectedId, {
      ...entryEditor,
      productVariantId: entryEditor.productVariantId ?? "",
      unitPriceMinor: entryEditor.unitPriceMinor ?? 0
    });
    setEntryEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ السعر" : "Price saved" });
    setEntries(await getPriceListEntries(selectedId));
    onChanged();
  };

  const saveAssignment = async () => {
    if (!assignmentEditor) return;
    const branchId = assignmentEditor.branchId ?? branchOptions[0]?.id ?? "";
    const priceListId = assignmentEditor.priceListId ?? selectedId ?? lists[0]?.id ?? "";
    if (!branchId || !priceListId) return;
    await assignPriceList({
      branchId,
      priceListId,
      priority: assignmentEditor.priority ?? 0,
      isActive: assignmentEditor.isActive ?? true,
      effectiveFrom: assignmentEditor.effectiveFrom ?? new Date().toISOString(),
      effectiveUntil: assignmentEditor.effectiveUntil ?? null
    });
    setAssignmentEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم ربط قائمة الأسعار بالفرع" : "Price list assigned to branch" });
    await load();
    onChanged();
  };

  const variants = products.flatMap((product) => product.variants.map((variant) => ({
    product,
    variant
  })));
  const storedContext = getStoredContext();
  const branchOptions = branches.length
    ? branches.map((branch) => ({ id: branch.id, name: branch.name }))
    : storedContext?.branchId
      ? [{ id: storedContext.branchId, name: storedContext.branchName }]
      : [];

  return (
    <Stack>
      <Panel title={ar ? "قوائم الأسعار" : "Price Lists"} actions={canManage ? <Button onClick={() => setListEditor({ code: "NEW-PRICE", nameAr: "قائمة جديدة", nameEn: "New Price List", scope: "Organization", currency: "AED", pricesIncludeTax: true, isDefault: false, isActive: true, priority: 0, effectiveFrom: new Date().toISOString(), effectiveUntil: null })}>{ar ? "قائمة جديدة" : "New list"}</Button> : null}>
        <div className="price-list-grid">
          {lists.map((list) => (
            <button key={list.id} className={selectedId === list.id ? "is-active" : ""} onClick={() => setSelectedId(list.id)}>
              <span><strong>{ar ? list.nameAr : list.nameEn}</strong><small>{list.code} · {list.currency}</small></span>
              <StatusBadge tone={list.isDefault ? "info" : "neutral"}>{list.isDefault ? (ar ? "افتراضية" : "Default") : `${list.entryCount} ${ar ? "سعر" : "prices"}`}</StatusBadge>
            </button>
          ))}
        </div>
      </Panel>

      <Panel title={ar ? "أسعار المتغيرات" : "Variant Prices"} actions={canManage && selectedId ? <Button onClick={() => setEntryEditor({ productVariantId: variants[0]?.variant.id ?? "", unitPriceMinor: 0, compareAtPriceMinor: null, effectiveFrom: new Date().toISOString(), effectiveUntil: null })}>{ar ? "إضافة سعر" : "Add price"}</Button> : null}>
        <DataTable
          rows={entries}
          rowKey={(row) => row.id}
          columns={[
            { key: "product", header: ar ? "المنتج" : "Product", cell: (row) => <button className="catalog-link" onClick={() => setEntryEditor(row)}><strong>{ar ? row.productNameAr : row.productNameEn}</strong><small>{row.sku} · {ar ? row.variantNameAr : row.variantNameEn}</small></button> },
            { key: "price", header: ar ? "السعر" : "Price", cell: (row) => formatMoneyMinor(row.unitPriceMinor, lists.find((item) => item.id === selectedId)?.currency ?? currency, ar ? "ar-AE" : "en-AE") },
            { key: "compare", header: ar ? "قبل الخصم" : "Compare at", cell: (row) => row.compareAtPriceMinor == null ? "—" : formatMoneyMinor(row.compareAtPriceMinor, lists.find((item) => item.id === selectedId)?.currency ?? currency, ar ? "ar-AE" : "en-AE") },
            { key: "from", header: ar ? "من" : "From", cell: (row) => new Date(row.effectiveFrom).toLocaleDateString() }
          ]}
        />
      </Panel>

      <Panel
        data-testid="price-list-assignments-panel"
        title={ar ? "ربط قوائم الأسعار بالفروع" : "Branch Price List Assignments"}
        actions={canManage && lists.length && branchOptions.length ? <Button data-testid="assign-price-list" onClick={() => setAssignmentEditor({ branchId: branchOptions[0]?.id ?? "", priceListId: selectedId || lists[0]?.id || "", priority: 100, isActive: true, effectiveFrom: new Date().toISOString(), effectiveUntil: null })}>{ar ? "ربط بفرع" : "Assign to branch"}</Button> : null}
      >
        {assignments.length ? <DataTable
          rows={assignments}
          rowKey={(row) => row.id}
          columns={[
            { key: "branch", header: ar ? "الفرع" : "Branch", cell: (row) => <strong>{row.branchName}</strong> },
            { key: "list", header: ar ? "قائمة الأسعار" : "Price list", cell: (row) => row.priceListName },
            { key: "priority", header: ar ? "الأولوية" : "Priority", cell: (row) => row.priority },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? (ar ? "نشط" : "Active") : (ar ? "متوقف" : "Inactive")}</StatusBadge> },
            { key: "action", header: "", cell: (row) => canManage ? <Button size="sm" variant="secondary" onClick={() => setAssignmentEditor(row)}>{ar ? "تعديل" : "Edit"}</Button> : null }
          ]}
        /> : <EmptyState title={ar ? "لا توجد روابط أسعار للفروع" : "No branch price assignments"} description={ar ? "اربط قائمة أسعار بفرع لتحديد أولوية التسعير الفعالة في الكاشير." : "Assign a price list to a branch to govern effective POS pricing priority."} />}
      </Panel>

      <Dialog open={Boolean(listEditor)} onClose={() => setListEditor(null)} title={ar ? "قائمة الأسعار" : "Price list"} footer={<Inline><Button variant="secondary" onClick={() => setListEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={() => void saveList()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {listEditor ? <Grid columns={2}><Field label={ar ? "الرمز" : "Code"} value={listEditor.code ?? ""} onChange={(e) => setListEditor({ ...listEditor, code: e.target.value.toUpperCase() })} /><Field label={ar ? "العملة" : "Currency"} value={listEditor.currency ?? "AED"} onChange={(e) => setListEditor({ ...listEditor, currency: e.target.value.toUpperCase() })} /><Field label={ar ? "الاسم العربي" : "Arabic name"} value={listEditor.nameAr ?? ""} onChange={(e) => setListEditor({ ...listEditor, nameAr: e.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={listEditor.nameEn ?? ""} onChange={(e) => setListEditor({ ...listEditor, nameEn: e.target.value })} /><Switch label={ar ? "تشمل الضريبة" : "Prices include tax"} checked={listEditor.pricesIncludeTax ?? true} onChange={(e) => setListEditor({ ...listEditor, pricesIncludeTax: e.target.checked })} /><Switch label={ar ? "افتراضية" : "Default"} checked={listEditor.isDefault ?? false} onChange={(e) => setListEditor({ ...listEditor, isDefault: e.target.checked })} /></Grid> : null}
      </Dialog>

      <Dialog open={Boolean(entryEditor)} onClose={() => setEntryEditor(null)} title={ar ? "سعر المتغير" : "Variant price"} footer={<Inline><Button variant="secondary" onClick={() => setEntryEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={() => void saveEntry()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {entryEditor ? <Grid columns={2}><SelectField label={ar ? "المتغير" : "Variant"} value={entryEditor.productVariantId ?? ""} onChange={(e) => setEntryEditor({ ...entryEditor, productVariantId: e.target.value })}>{variants.map(({ product, variant }) => <option key={variant.id} value={variant.id}>{ar ? product.nameAr : product.nameEn} · {ar ? variant.nameAr : variant.nameEn}</option>)}</SelectField><MoneyField label={ar ? "السعر" : "Price"} valueMinor={entryEditor.unitPriceMinor ?? 0} currency={lists.find((item) => item.id === selectedId)?.currency ?? currency} onMinorChange={(value) => setEntryEditor({ ...entryEditor, unitPriceMinor: value ?? 0 })} /><MoneyField label={ar ? "السعر المقارن" : "Compare-at price"} valueMinor={entryEditor.compareAtPriceMinor ?? null} currency={lists.find((item) => item.id === selectedId)?.currency ?? currency} nullable onMinorChange={(value) => setEntryEditor({ ...entryEditor, compareAtPriceMinor: value })} /></Grid> : null}
      </Dialog>

      <Dialog open={Boolean(assignmentEditor)} onClose={() => setAssignmentEditor(null)} title={ar ? "ربط قائمة أسعار بفرع" : "Assign price list to branch"} footer={<Inline><Button variant="secondary" onClick={() => setAssignmentEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-price-list-assignment" disabled={!canManage} onClick={() => void saveAssignment()}>{ar ? "حفظ الربط" : "Save assignment"}</Button></Inline>}>
        {assignmentEditor ? <Grid columns={2}>
          <SelectField label={ar ? "الفرع" : "Branch"} value={assignmentEditor.branchId ?? ""} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, branchId: event.target.value })}>{branchOptions.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}</SelectField>
          <SelectField label={ar ? "قائمة الأسعار" : "Price list"} value={assignmentEditor.priceListId ?? ""} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, priceListId: event.target.value })}>{lists.map((list) => <option key={list.id} value={list.id}>{ar ? list.nameAr : list.nameEn} · {list.currency}</option>)}</SelectField>
          <Field label={ar ? "الأولوية" : "Priority"} type="number" value={assignmentEditor.priority ?? 0} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, priority: Number(event.target.value) })} />
          <Field label={ar ? "ساري من" : "Effective from"} type="datetime-local" value={(assignmentEditor.effectiveFrom ?? new Date().toISOString()).slice(0, 16)} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, effectiveFrom: new Date(event.target.value).toISOString() })} />
          <Field label={ar ? "ساري حتى" : "Effective until"} type="datetime-local" value={assignmentEditor.effectiveUntil?.slice(0, 16) ?? ""} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, effectiveUntil: event.target.value ? new Date(event.target.value).toISOString() : null })} />
          <Switch label={ar ? "نشط" : "Active"} checked={assignmentEditor.isActive ?? true} onChange={(event) => setAssignmentEditor({ ...assignmentEditor, isActive: event.target.checked })} />
        </Grid> : null}
      </Dialog>
    </Stack>
  );
}

function TaxPanel({
  language,
  canManage,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [items, setItems] = useState<TaxCode[]>([]);
  const [editor, setEditor] = useState<Partial<TaxCode> | null>(null);
  const load = async () => setItems(await getTaxCodes());
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!editor) return;
    await saveTaxCode({
      ...editor,
      code: editor.code ?? "",
      nameAr: editor.nameAr ?? "",
      nameEn: editor.nameEn ?? "",
      treatment: editor.treatment ?? "TaxInclusive",
      rateBasisPoints: editor.rateBasisPoints ?? 0
    });
    setEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ رمز الضريبة" : "Tax code saved" });
    await load();
    onChanged();
  };

  return (
    <Panel title={ar ? "محرك الضرائب" : "Tax Engine"} actions={canManage ? <Button onClick={() => setEditor({ code: "NEW-TAX", nameAr: "ضريبة جديدة", nameEn: "New Tax", treatment: "TaxInclusive", rateBasisPoints: 500, reportingCode: "STANDARD", isDefault: false, isActive: true, effectiveFrom: new Date().toISOString(), effectiveUntil: null })}>{ar ? "رمز جديد" : "New code"}</Button> : null}>
      <DataTable
        rows={items}
        rowKey={(row) => row.id}
        columns={[
          { key: "code", header: ar ? "الرمز" : "Code", cell: (row) => <button className="catalog-link" onClick={() => setEditor(row)}><strong>{row.code}</strong><small>{ar ? row.nameAr : row.nameEn}</small></button> },
          { key: "treatment", header: ar ? "المعاملة" : "Treatment", cell: (row) => row.treatment },
          { key: "rate", header: ar ? "النسبة" : "Rate", cell: (row) => `${row.rateBasisPoints / 100}%` },
          { key: "default", header: ar ? "الافتراضي" : "Default", cell: (row) => <StatusBadge tone={row.isDefault ? "info" : "neutral"}>{row.isDefault ? "Default" : "—"}</StatusBadge> },
          { key: "active", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "neutral"}>{row.isActive ? "Active" : "Inactive"}</StatusBadge> }
        ]}
      />
      <Dialog open={Boolean(editor)} onClose={() => setEditor(null)} title={ar ? "رمز الضريبة" : "Tax code"} footer={<Inline><Button variant="secondary" onClick={() => setEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage} onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {editor ? <Grid columns={2}><Field label={ar ? "الرمز" : "Code"} value={editor.code ?? ""} onChange={(e) => setEditor({ ...editor, code: e.target.value.toUpperCase() })} /><SelectField label={ar ? "المعاملة" : "Treatment"} value={editor.treatment ?? "TaxInclusive"} onChange={(e) => setEditor({ ...editor, treatment: e.target.value as TaxCode["treatment"] })}>{["TaxInclusive", "TaxExclusive", "ZeroRated", "Exempt"].map((value) => <option key={value}>{value}</option>)}</SelectField><Field label={ar ? "الاسم العربي" : "Arabic name"} value={editor.nameAr ?? ""} onChange={(e) => setEditor({ ...editor, nameAr: e.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={editor.nameEn ?? ""} onChange={(e) => setEditor({ ...editor, nameEn: e.target.value })} /><Field label={ar ? "النقاط الأساسية" : "Basis points"} type="number" min={0} max={10000} value={editor.rateBasisPoints ?? 0} onChange={(e) => setEditor({ ...editor, rateBasisPoints: Number(e.target.value) })} /><Switch label={ar ? "افتراضي" : "Default"} checked={editor.isDefault ?? false} onChange={(e) => setEditor({ ...editor, isDefault: e.target.checked })} /></Grid> : null}
      </Dialog>
    </Panel>
  );
}

function PromotionsPanel({
  language,
  currency,
  canManage,
  onChanged
}: {
  language: Language;
  currency: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [items, setItems] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editor, setEditor] = useState<Partial<Promotion> | null>(null);
  const promotionReadVersion = useRef(0);
  const targetSelectionMissing = Boolean(editor
    && (editor.targetType === "Product" || editor.targetType === "Category")
    && !editor.targetId);
  const targetOptions = editor?.targetType === "Product" ? products : categories;
  const load = async () => {
    const readVersion = ++promotionReadVersion.current;
    const [promotions, productItems, categoryItems] = await Promise.all([getPromotions(), getProducts(), getCategories()]);
    if (readVersion !== promotionReadVersion.current) return;
    setItems(promotions);
    setProducts(productItems);
    setCategories(categoryItems);
  };
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!editor || targetSelectionMissing) return;
    promotionReadVersion.current++;
    const saved = await savePromotion({
      ...editor,
      code: editor.code ?? "",
      nameAr: editor.nameAr ?? "",
      nameEn: editor.nameEn ?? "",
      promotionType: editor.promotionType ?? "Percentage",
      status: editor.status ?? "Draft",
      targetType: editor.targetType ?? "AllProducts"
    });
    // Publish the committed response before closing: reopening must not copy the
    // stale row while the background list refresh is still pending.
    setItems(current => current.some(item => item.id === saved.id)
      ? current.map(item => item.id === saved.id ? saved : item)
      : [...current, saved]);
    setEditor(null);
    toast.notify({ tone: "success", title: ar ? "تم حفظ العرض" : "Promotion saved" });
    await load();
    onChanged();
  };

  return (
    <Panel title={ar ? "العروض والخصومات" : "Promotions & Discounts"} actions={canManage ? <Button onClick={() => setEditor({ code: "NEW-PROMO", nameAr: "عرض جديد", nameEn: "New Promotion", promotionType: "Percentage", status: "Draft", targetType: "AllProducts", targetId: null, discountBasisPoints: 1000, discountAmountMinor: null, buyQuantity: null, getQuantity: null, maximumDiscountMinor: null, minimumSpendMinor: null, enabledServiceTypes: ["WalkIn", "DineIn", "Takeaway", "Delivery"], isCombinable: false, priority: 0, startsAt: new Date().toISOString(), endsAt: new Date(Date.now() + 365 * 86400000).toISOString(), usageLimit: null, usageCount: 0 })}>{ar ? "عرض جديد" : "New promotion"}</Button> : null}>
      <DataTable
        rows={items}
        rowKey={(row) => row.id}
        columns={[
          { key: "name", header: ar ? "العرض" : "Promotion", cell: (row) => <button className="catalog-link" onClick={() => setEditor(row)}><strong>{ar ? row.nameAr : row.nameEn}</strong><small>{row.code} · {row.promotionType}</small></button> },
          { key: "target", header: ar ? "الهدف" : "Target", cell: (row) => row.targetNameEn ?? row.targetType },
          { key: "discount", header: ar ? "القيمة" : "Benefit", cell: (row) => row.promotionType === "Percentage" ? `${(row.discountBasisPoints ?? 0) / 100}%` : row.promotionType === "FixedAmount" ? formatMoneyMinor(row.discountAmountMinor ?? 0, currency, ar ? "ar-AE" : "en-AE") : `${row.buyQuantity}+${row.getQuantity}` },
          { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : row.status === "Draft" ? "warning" : "neutral"}>{row.status}</StatusBadge> }
        ]}
      />
      <Dialog open={Boolean(editor)} onClose={() => setEditor(null)} title={ar ? "العرض" : "Promotion"} footer={<Inline><Button variant="secondary" onClick={() => setEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!canManage || targetSelectionMissing} onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {editor ? <Stack>{targetSelectionMissing ? <small data-testid="promotion-target-required">{ar ? "اختر المنتج أو الفئة المستهدفة قبل الحفظ." : "Select the target product or category before saving."}</small> : null}<Grid columns={2}><Field label={ar ? "الرمز" : "Code"} value={editor.code ?? ""} onChange={(e) => setEditor({ ...editor, code: e.target.value.toUpperCase() })} /><SelectField label={ar ? "الحالة" : "Status"} value={editor.status ?? "Draft"} onChange={(e) => setEditor({ ...editor, status: e.target.value as Promotion["status"] })}>{["Draft", "Active", "Paused", "Expired", "Archived"].map((value) => <option key={value}>{value}</option>)}</SelectField><Field label={ar ? "الاسم العربي" : "Arabic name"} value={editor.nameAr ?? ""} onChange={(e) => setEditor({ ...editor, nameAr: e.target.value })} /><Field label={ar ? "الاسم الإنجليزي" : "English name"} value={editor.nameEn ?? ""} onChange={(e) => setEditor({ ...editor, nameEn: e.target.value })} /><SelectField label={ar ? "نوع العرض" : "Promotion type"} value={editor.promotionType ?? "Percentage"} onChange={(e) => setEditor({ ...editor, promotionType: e.target.value as Promotion["promotionType"] })}>{["Percentage", "FixedAmount", "BuyXGetY"].map((value) => <option key={value}>{value}</option>)}</SelectField><SelectField label={ar ? "نوع الهدف" : "Target type"} value={editor.targetType ?? "AllProducts"} onChange={(e) => setEditor({ ...editor, targetType: e.target.value as Promotion["targetType"], targetId: null })}>{["AllProducts", "Category", "Product", "Variant"].map((value) => <option key={value}>{value}</option>)}</SelectField>{editor.targetType === "Category" ? <SelectField label={ar ? "الفئة" : "Category"} value={editor.targetId ?? ""} onChange={(e) => setEditor({ ...editor, targetId: e.target.value })}><option value="">{ar ? "اختر الفئة" : "Select a category"}</option>{editor.targetId && !targetOptions.some(item => item.id === editor.targetId) ? <option value={editor.targetId}>{ar ? "الاختيار المحفوظ غير ظاهر في القائمة" : "Saved target is not in this list"}</option> : null}{categories.map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}</SelectField> : editor.targetType === "Product" ? <SelectField label={ar ? "المنتج" : "Product"} value={editor.targetId ?? ""} onChange={(e) => setEditor({ ...editor, targetId: e.target.value })}><option value="">{ar ? "اختر المنتج" : "Select a product"}</option>{editor.targetId && !targetOptions.some(item => item.id === editor.targetId) ? <option value={editor.targetId}>{ar ? "الاختيار المحفوظ غير ظاهر في القائمة" : "Saved target is not in this list"}</option> : null}{products.map((item) => <option key={item.id} value={item.id}>{ar ? item.nameAr : item.nameEn}</option>)}</SelectField> : null}<Field label={ar ? "نسبة الخصم بالنقاط الأساسية" : "Discount basis points"} type="number" min={0} max={10000} value={editor.discountBasisPoints ?? ""} onChange={(e) => setEditor({ ...editor, discountBasisPoints: e.target.value ? Number(e.target.value) : null })} /><MoneyField label={ar ? "مبلغ الخصم" : "Discount amount"} valueMinor={editor.discountAmountMinor ?? null} currency={currency} nullable onMinorChange={(value) => setEditor({ ...editor, discountAmountMinor: value })} /><MoneyField label={ar ? "الحد الأدنى للشراء" : "Minimum spend"} valueMinor={editor.minimumSpendMinor ?? null} currency={currency} nullable onMinorChange={(value) => setEditor({ ...editor, minimumSpendMinor: value })} /><Field label={ar ? "الأولوية" : "Priority"} type="number" value={editor.priority ?? 0} onChange={(e) => setEditor({ ...editor, priority: Number(e.target.value) })} /></Grid><Checkbox data-testid="promotion-is-combinable" label={ar ? "السماح بجمع هذا العرض مع قسيمة متوافقة" : "Allow combination with an eligible coupon"} checked={editor.isCombinable ?? false} disabled={!canManage} onChange={(e) => setEditor({ ...editor, isCombinable: e.target.checked })} /><small>{ar ? "تبقى شروط أهلية القسيمة وحدود الاستخدام مطبقة." : "Coupon eligibility and usage limits still apply."}</small></Stack> : null}
      </Dialog>
    </Panel>
  );
}
